import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/hooks/use-theme-context';
import {
  FieldErrors,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFormRegister,
  UseFormSetValue
} from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import Input from '@components/atoms/ui-kit/input';
import Checkbox from '@components/atoms/ui-kit/checkbox';
import Button from '@components/atoms/ui-kit/button';
import {
  WizardFormData,
  WizardUser,
  ROLE_OPTIONS
} from '../create-customer-wizard-types';

interface UserField {
  id: string;
}

interface Props {
  register: UseFormRegister<WizardFormData>;
  errors: FieldErrors<WizardFormData>;
  setValue: UseFormSetValue<WizardFormData>;
  watchedValues: WizardFormData;
  userFields: UserField[];
  appendUser: UseFieldArrayAppend<WizardFormData, 'users'>;
  removeUser: UseFieldArrayRemove;
}

export function CustomerWizardStep3({
  register,
  errors,
  setValue,
  watchedValues,
  userFields,
  appendUser,
  removeUser
}: Props) {
  const { t } = useTranslation();
  const { getColour } = useThemeContext();

  const emptyUser: WizardUser = {
    email: '',
    first_name: '',
    last_name: '',
    roles: ['admin']
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4
            className="text-sm font-medium"
            style={{ color: getColour('normalText') }}
          >
            {t('users')}
          </h4>
          <p className="text-xs" style={{ color: getColour('inactiveText') }}>
            {t('users-step-description')}
          </p>
        </div>
        <Button
          icon={<Plus className="h-4 w-4" />}
          label={t('add-user')}
          size="small"
          variant="smoke"
          onClick={() => appendUser(emptyUser)}
        />
      </div>

      {userFields.length === 0 ? (
        <p
          className="text-sm p-4 rounded-2xl text-center"
          style={{
            backgroundColor: getColour('thirdLayer'),
            color: getColour('inactiveText')
          }}
        >
          {t('no-users-added')}
        </p>
      ) : (
        <div className="space-y-4 max-h-80 overflow-y-auto">
          {userFields.map((field, index) => (
            <div
              key={field.id}
              className="p-4 rounded-2xl border"
              style={{
                borderColor: getColour('hairline'),
                backgroundColor: getColour('appSoft')
              }}
            >
              <div className="flex items-center justify-between mb-3">
                <span
                  className="text-sm font-medium"
                  style={{ color: getColour('normalText') }}
                >
                  {t('user')} {index + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removeUser(index)}
                  className="p-1 rounded-2xl hover:bg-red-100"
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input
                  label={t('email')}
                  type="email"
                  {...register(`users.${index}.email`)}
                  error={errors.users?.[index]?.email?.message}
                  required
                />
                <Input
                  label={t('first-name')}
                  {...register(`users.${index}.first_name`)}
                  error={errors.users?.[index]?.first_name?.message}
                  required
                />
                <Input
                  label={t('last-name')}
                  {...register(`users.${index}.last_name`)}
                  error={errors.users?.[index]?.last_name?.message}
                  required
                />
              </div>

              <div className="mt-3">
                <label
                  className="block text-xs font-medium mb-2"
                  style={{ color: getColour('inactiveText') }}
                >
                  {t('roles')}
                </label>
                <div className="grid grid-cols-4">
                  {ROLE_OPTIONS.map((role, i) => {
                    const currentRoles =
                      watchedValues.users?.[index]?.roles || [];
                    const isChecked = currentRoles.includes(role.value);
                    return (
                      <div
                        key={role.value}
                        className="flex items-center justify-center px-3 py-2"
                        style={{
                          borderRight:
                            i < ROLE_OPTIONS.length - 1
                              ? `1px solid ${getColour('hairline')}`
                              : undefined
                        }}
                      >
                        <Checkbox
                          label={role.label}
                          checked={isChecked}
                          onChange={e => {
                            const newRoles = e.target.checked
                              ? [...currentRoles, role.value]
                              : currentRoles.filter(r => r !== role.value);
                            setValue(`users.${index}.roles`, newRoles);
                          }}
                          wrapperClass="!p-0"
                          className="size-3.5"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
